import {
  dec,
  decimalPlaces,
  mulDivRound,
  mulRound,
  prorate,
  sum,
  toFixed,
  ZERO,
  type Dec,
} from './decimal';

/**
 * Document totals, mirroring the backend's engine (ACCOUNTING_RULES §1 and §3) so the screen shows the
 * same numbers the server will post:
 *
 * - per line `gross = round(qty × unit price)`; the line discount (amount or %) comes off the gross;
 * - a document discount is prorated over the discounted lines by the largest-remainder method;
 * - tax per line on what remains — exclusive `tax = round(net × rate)`, inclusive
 *   `net = round(amount / (1 + rate))`, `tax = amount − net`;
 * - everything is rounded at line level (ROUND_HALF_UP) and document totals are plain sums.
 *
 * Unlike the server this never throws: an invalid discount is clamped and reported in `errors`.
 */

export type DiscountType = 'AMOUNT' | 'PERCENT';

export interface Discount {
  type: DiscountType;
  value: string;
}

export interface TotalsLineInput {
  /** Quantity in the entered unit. */
  qty: string;
  /** Price per entered unit. */
  unitPrice: string;
  discount?: Discount | null;
  /** Percent, e.g. "17". */
  taxRate?: string | null;
}

export interface TotalsOptions {
  decimals: number;
  taxEnabled: boolean;
  pricesIncludeTax: boolean;
  documentDiscount?: Discount | null;
}

export interface LineTotals {
  gross: string;
  lineDiscount: string;
  documentDiscount: string;
  net: string;
  taxRate: string;
  tax: string;
  total: string;
  error?: string;
}

export interface DocumentTotals {
  lines: LineTotals[];
  subtotal: string;
  documentDiscount: string;
  /** Line discounts + document discount. */
  discountTotal: string;
  netTotal: string;
  taxTotal: string;
  total: string;
  /** Problems the server would refuse (e.g. a discount larger than the amount). */
  errors: string[];
}

const HUNDRED = dec('100');

/** Discount amount on `base`; clamped to [0, base] with a message when out of range. */
export function discountAmount(
  discount: Discount | null | undefined,
  base: Dec,
  decimals: number,
): { amount: Dec; error?: string } {
  if (!discount || discount.value.trim() === '') return { amount: ZERO };
  const value = dec(discount.value);
  if (value < 0n) return { amount: ZERO, error: 'A discount cannot be negative.' };
  if (discount.type === 'PERCENT') {
    if (value > HUNDRED) return { amount: base, error: 'A percentage discount is at most 100.' };
    return { amount: mulDivRound(base, value, HUNDRED, decimals) };
  }
  if (decimalPlaces(discount.value) > decimals)
    return {
      amount: value > base ? base : value,
      error: `A discount amount has at most ${String(decimals)} decimals.`,
    };
  if (value > base) return { amount: base, error: 'The discount is larger than the amount.' };
  return { amount: value };
}

export function computeTotals(lines: TotalsLineInput[], options: TotalsOptions): DocumentTotals {
  const { decimals } = options;
  const fixed = (v: Dec) => toFixed(v, decimals);
  const errors: string[] = [];

  const first = lines.map((line) => {
    const price = dec(line.unitPrice);
    const gross = mulRound(dec(line.qty), price < 0n ? ZERO : price, decimals);
    const d = discountAmount(line.discount, gross, decimals);
    if (d.error) errors.push(d.error);
    return { line, gross, lineDiscount: d.amount, afterLine: gross - d.amount, error: d.error };
  });

  const base = sum(first.map((l) => l.afterLine));
  const doc = discountAmount(options.documentDiscount, base, decimals);
  if (doc.error) errors.push(doc.error);
  const shares =
    base === 0n
      ? first.map(() => ZERO)
      : prorate(
          doc.amount,
          first.map((l) => (l.afterLine < 0n ? ZERO : l.afterLine)),
          decimals,
        );

  const result = first.map((l, i) => {
    const share = shares[i] ?? ZERO;
    const amount = l.afterLine - share;
    const rate = options.taxEnabled ? dec(l.line.taxRate ?? '0') : ZERO;
    let net: Dec;
    let tax: Dec;
    if (options.pricesIncludeTax) {
      net = mulDivRound(amount, HUNDRED, HUNDRED + rate, decimals);
      tax = amount - net;
    } else {
      net = amount;
      tax = mulDivRound(net, rate, HUNDRED, decimals);
    }
    return { gross: l.gross, lineDiscount: l.lineDiscount, share, net, rate, tax, error: l.error };
  });

  return {
    lines: result.map((r) => ({
      gross: fixed(r.gross),
      lineDiscount: fixed(r.lineDiscount),
      documentDiscount: fixed(r.share),
      net: fixed(r.net),
      taxRate: toFixed(r.rate, 4),
      tax: fixed(r.tax),
      total: fixed(r.net + r.tax),
      ...(r.error ? { error: r.error } : {}),
    })),
    subtotal: fixed(sum(result.map((r) => r.gross))),
    documentDiscount: fixed(sum(result.map((r) => r.share))),
    discountTotal: fixed(sum(result.map((r) => r.lineDiscount + r.share))),
    netTotal: fixed(sum(result.map((r) => r.net))),
    taxTotal: fixed(sum(result.map((r) => r.tax))),
    total: fixed(sum(result.map((r) => r.net + r.tax))),
    errors,
  };
}

/** A discount worth sending to the API (zero or empty discounts are omitted). */
export function effectiveDiscount(discount: Discount | null | undefined): Discount | undefined {
  if (!discount || discount.value.trim() === '') return undefined;
  return dec(discount.value) > 0n
    ? { type: discount.type, value: discount.value.trim() }
    : undefined;
}
